import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ApplicationType, ResourcePropertyDto } from 'src/app/services/dtos'
import { Arn } from 'src/utils/types'
import { ResourceType, ResourceTypeDetails, ResourceVariant } from 'src/utils/resource-types'
import { ResourcesTab } from 'src/app/account/components/application/application.component'
import { ProcessingStatus } from '../process-state/process-state.component'
import { RaisedIssue } from '../../services/issue'
import { Table } from 'primeng/table'
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators'
import { SelectItem } from 'primeng/api'
import { NavigationEnd, Router } from '@angular/router'
import { BehaviorSubject, Subscription } from 'rxjs'
import { UiService } from 'src/app/services/ui.service'
import { LoggerService } from '../../services/logger.service'
import { ResourceConfig, S3BucketConfig } from '../../services/sync-pair.service'
import { Application } from '../../services/application.service'
import { handleArpioError } from 'src/utils/handle-error'

export enum ResourceStatus {
  // these values are the display-precedence used to arbitrate between showing staging
  // and sync status for a given resource when we're showing combined status.
  NO_STATUS = 0,
  SUCCEEDED = 1,
  PENDING = 2,
  PROCESSING = 3,
  FAILED = 4,
  QUARANTINED = 5,
  SCAN_CLEAN = 6,
  SCAN_INFECTED = 7,
  SCAN_FAILED = 8,
}

const QUARANTINE_STATUS_TYPES = new Set([
  ResourceStatus.QUARANTINED,
  ResourceStatus.SCAN_CLEAN,
  ResourceStatus.SCAN_INFECTED,
  ResourceStatus.SCAN_FAILED,
])

const S3_OBJECT_REPLICATION_MODES = new Map([
  ['none', 'Bucket only'],
  ['realTime', 'Bucket and contents'],
  ['snapshot', 'Bucket and contents (Snapshot)'],
  [undefined, 'Bucket only (not configured)'],
])

export function isStatusQuarantineType(status: ResourceStatus): boolean {
  return QUARANTINE_STATUS_TYPES.has(status)
}

export interface ViewableApplicationResource {
  resourceType: ResourceType
  resourceVariant: ResourceVariant
  resourceArn: Arn
  sourceResourceName: string
  sourceResourceGroup?: string
  targetResourceName: string
  targetResourceGroup?: string
  resourceProperties: Array<ResourcePropertyDto>
  status: ResourceStatus
  progress: number
  message: string
  issues: Set<RaisedIssue>
  displayResourceType: string
  sourceResourcePortalUri?: string
  targetResourcePortalUri?: string
  resourcePortalName?: string
  resourceTypeDetails: ResourceTypeDetails
}

export interface MultiSelectCategory {
  name: number | string
  label: string
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'resource-status-pill',
  templateUrl: './resource-status-pill.component.html',
  styleUrls: ['./resource-status-pill.component.scss'],
  standalone: false,
})
export class ResourceStatusPillComponent implements OnInit {
  @Input() status: ResourceStatus
  @Input() progress: 0

  get compositeProgress() {
    if (this.status === ResourceStatus.PROCESSING) {
      return this.progress
    }
    return 1
  }

  get compositeProgressPercent() {
    return Math.round(this.compositeProgress * 100)
  }

  ngOnInit() {}
}

@Component({
  selector: 'app-resources-view',
  templateUrl: './app-resources-view.component.html',
  styleUrls: ['./app-resources-view.component.scss'],
  standalone: false,
})
export class AppResourcesViewComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  expandedRows = new Set<string>()

  @Input() resources: ViewableApplicationResource[]
  @Input() tabType = ResourcesTab.RECOVERY
  @Input() appStatus: ProcessingStatus = null
  @Input() appIsDeleted: boolean = false
  @Input() loading: boolean = false
  @Input() application: Application = null
  @Input() initialFilterText: string = '' // Filter text passed from parent to persist across component recreation

  @Output() filterTextChange = new EventEmitter<string>() // Emit filter changes to parent for persistence

  // Cached values to avoid repeated iterations in template
  private _shouldShowResourceGroupColumn: boolean = false
  private _globalFilterFields: string[] = []
  private _notEmptyList: boolean = false

  private lastEmittedFilterText: string = ''
  filterText: string = ''
  lastFilterEvent: Event | null = null // Added to store the last filter event

  @ViewChild('dt1') resourcesTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  resourceCategory: MultiSelectCategory[] = []
  selectedCategories: string[]
  selectedStatuses: number[]
  previousUrl: string | undefined
  statusCategories: MultiSelectCategory[]
  isFilterApplied: boolean = false
  representatives!: any[]
  totalRecords = 0
  initializeRouteListener$: Subscription
  debounceSubject: BehaviorSubject<string> = new BehaviorSubject<string>('') // A subject to handle debounce logic for search input delay
  loadingFilterInput: boolean = false // Loading flag to control the spinner visibility
  resourceConfigs: Map<string, ResourceConfig> = new Map<string, ResourceConfig>() // Arn -> resourceConfig
  statusesLabels: MultiSelectCategory[] = [
    { label: 'OK', name: 1 },
    { label: 'PENDING', name: 2 },
    { label: 'PROCESSING', name: 3 },
    { label: 'FAILED', name: 4 },
    { label: 'QUARANTINED', name: 5 },
    { label: 'CLEAN', name: 6 },
    { label: 'INFECTED', name: 7 },
    { label: 'SCAN-FAILED', name: 8 },
    { label: 'NONE', name: 0 },
  ]

  constructor(
    private router: Router,
    private uiService: UiService,
    private logger: LoggerService
  ) {
    this.adjustScrollHeight()
    this.initializeRouteListener()
  }

  initializeRouteListener() {
    this.initializeRouteListener$ = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          // Check if the previous URL is different from the current URL
          if (this.previousUrl && this.previousUrl !== event.url) {
            this.clear(this.resourcesTable) // Your method to clear the table
            this.transferkeywordToAllApplications(this.filterText)
          }
          // Update the previous URL for the next comparison
          this.previousUrl = event.url
        })
      )
      .subscribe(() => {})
  }

  /**
   * Manages the filtering of resources based on the provided filterText.
   * Updates the input field and applies the filter to the resources table.
   * If the filterText is empty, clears the input and resets the filter.
   *
   * @param {string} filterText - ARP-2910
   *  The text used for filtering the resources.
   *  If empty, clears the input field and resets the filter.
   *  If non-empty, applies the filter to the resources table.
   */
  transferkeywordToAllApplications(filterText: string) {
    this.filterInput.nativeElement.value = filterText
    this.filterText = filterText
    this.debounceSubject.next(filterText)
  }

  ngOnInit() {
    this.getResourceTypeValues()
    this.logger.info('Resources view loaded', { component: 'ResourcesPage' })
  }

  ngOnDestroy(): void {
    this.clear(this.resourcesTable)
    this.initializeRouteListener$.unsubscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resources) {
      this.resources = changes.resources.currentValue
      this.getResourceTypeValues()

      if (this.resourcesTable && !this.resourcesTable.sortField) {
        // No custom sort has been applied.  Apply default sort ordering by type then status.
        this.resources = this.resources.sort((a, b) => {
          const typeCompare = a.displayResourceType.localeCompare(b.displayResourceType)
          if (typeCompare == 0) {
            return a.status - b.status
          } else {
            return typeCompare
          }
        })
      }
    }

    // Recompute cached values when relevant inputs change
    if (changes.resources || changes.application || changes.appIsDeleted) {
      this._shouldShowResourceGroupColumn =
        this.application?.type === ApplicationType.AZURE && !!this.resources?.some((r) => r.sourceResourceGroup)
      this.updateGlobalFilterFields()
    }

    if (changes.resources || changes.tabType || changes.appStatus) {
      this.updateNotEmptyList()
    }

    // Apply filter AFTER handling resources to avoid interfering with sort state
    if (this.lastFilterEvent) {
      // If there is a last filter event, reapply the filter
      this.applyFilter((this.lastFilterEvent.target as HTMLInputElement).value, this.resourcesTable)
    }
  }
  ngAfterViewInit() {
    if (this.resourcesTable != null) {
      this.resourcesTable.resetScrollTop = function () {}
    }
    this.debounceSubject
      .pipe(
        debounceTime(600),
        distinctUntilChanged(),
        tap(() => (this.loadingFilterInput = false))
      )
      .subscribe((filterText: string) => {
        this.applyFilter(filterText, this.resourcesTable)
      })

    // Initialize filter from parent's persisted value
    if (this.initialFilterText) {
      // Set lastEmittedFilterText to prevent emitting back to parent
      this.lastEmittedFilterText = this.initialFilterText.toLowerCase().trim()
      // ensure the view is fully initialized
      setTimeout(() => {
        this.transferkeywordToAllApplications(this.initialFilterText)
      }, 0)
    }
  }

  /**
   * Gets resources categories based on the resources array.
   */
  getResourceCategory() {
    // Use a Map to store unique categories with string keys for uniqueness
    const uniqueCategoriesMap = new Map<string, MultiSelectCategory>()

    this.resources.forEach((resource) => {
      // Create a unique key (resourceType) for each resource type based on 'label' and 'name'
      const key = resource.resourceType + ':' + resource.displayResourceType

      // Check if the key is already added to the map to ensure uniqueness
      if (!uniqueCategoriesMap.has(key)) {
        // If not present, add it to the Map
        uniqueCategoriesMap.set(key, {
          label: resource.resourceType,
          name: resource.displayResourceType,
        })
      }
    })

    // Convert the map values to an array for the resourceCategory
    this.resourceCategory = Array.from(uniqueCategoriesMap.values())
  }

  /**
   * Gets available status categories based on the resources and statusesLabels array.
   * Statuses with name value 0 will appear at the end of the result.
   */
  getAvailableStatuses(): void {
    // Use a Map to store unique status categories with a combination of label and name as the key
    const uniqueStatusMap = new Map<string, MultiSelectCategory>()

    // Iterate through resources and add unique status categories
    this.resources.forEach((resource) => {
      // Find the corresponding label for the status from statusesLabels
      const statusLabel = this.statusesLabels.find((status) => status.name === resource.status)
      if (statusLabel) {
        // Form a unique key using label and name to ensure uniqueness
        const key = statusLabel.label + ':' + resource.status

        // Add to the Map if not already present
        if (!uniqueStatusMap.has(key)) {
          uniqueStatusMap.set(key, {
            label: statusLabel.label,
            name: resource.status,
          })
        }
      }
    })

    // Convert the map values to an array
    const uniqueStatusArray = Array.from(uniqueStatusMap.values())

    // Sort the array so that items with name value 0 come at the end
    uniqueStatusArray.sort((a, b) => {
      const nameA = a.name
      const nameB = b.name
      if (nameA === 0 && nameB !== 0) {
        return 1 // 0 comes after other values
      } else if (nameA !== 0 && nameB === 0) {
        return -1 // Other values come before 0
      } else {
        return 0 // Maintain order for other values
      }
    })

    // Assign the sorted statuses to the statusCategories
    this.statusCategories = uniqueStatusArray
  }

  getResourceTypeValues() {
    this.getResourceCategory()
    this.getAvailableStatuses()
  }

  filterResourcesByCategory(selectedCategories: any[], filterDT?: any, event?: any): void {
    this.selectedCategories = selectedCategories
    filterDT(selectedCategories)
  }

  removeResCategoryTag(categoryToRemove: string, filter?: any, value?: any[]): void {
    value = value.filter((category) => category !== categoryToRemove)
    this.selectedCategories = value
    this.filterResourcesByCategory(value, filter)
  }

  filterStatusFunc(selectedStatuses: any[], filter?: any): void {
    this.selectedStatuses = selectedStatuses
    filter(selectedStatuses)
  }

  removeStatusTag(statusToRemove: SelectItem, value: any, filter: any): void {
    value = value.filter((category) => category !== statusToRemove)
    this.selectedStatuses = value
    // Re-filter resources based on updated selectedCategories
    this.filterStatusFunc(value, filter)
  }

  scrollHeight: string = '55vh' // Default value
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.adjustScrollHeight()
  }

  adjustScrollHeight() {
    // Example calculation (you might need to adjust this based on your layout)
    // Let's say you have a header of 347px height and a footer of 50px height that you want to exclude from the viewport height
    const headerFooterHeight = 347 + 50 // Example fixed height in pixels
    const viewportHeight = window.innerHeight // Viewport height in pixels
    const adjustedHeight = viewportHeight - headerFooterHeight // Adjusted height in pixels

    // Convert the adjusted height to 'vh' (Viewport Height) units
    const adjustedHeightVh = (adjustedHeight / viewportHeight) * 100
    this.scrollHeight = `${Math.round(adjustedHeightVh)}vh`
  }

  // Convert status label to corresponding status code
  mapLabelToStatusCode(label: string): number[] {
    const lowerCaseLabel = label.toLowerCase()
    return this.statusesLabels
      .filter((status) => status.label.toLowerCase().includes(lowerCaseLabel))
      .map((status) => status.name as number) // Returns array of matching status codes
  }

  filterGlobal(event: any): void {
    // Save the most recent event so we can retrigger filtering on ngOnChanges
    this.lastFilterEvent = event
    this.loadingFilterInput = true
    const filterText = event.target.value
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string, dt: Table): void {
    if (!dt || typeof filterText !== 'string') return
    // Preserve sort state before clearing filters
    const currentSortField = dt.sortField
    const currentSortOrder = dt.sortOrder

    dt.clearFilterValues() // Clear only filters, preserve sort state

    const filterValue = filterText.toLowerCase().trim()
    const matchingStatusCodes = this.mapLabelToStatusCode(filterValue)

    if (!filterValue) {
      this.filterText = filterValue
      this.isFilterApplied = false
      // Emit change to parent for persistence
      if (filterValue !== this.lastEmittedFilterText) {
        this.lastEmittedFilterText = filterValue
        this.filterTextChange.emit(filterValue)
      }
      // Still restore sort state even when clearing filter
      if (currentSortField) {
        dt.sortField = currentSortField
        dt.sortOrder = currentSortOrder
        // Force PrimeNG to re-evaluate sort state
        setTimeout(() => {
          if (dt.sortField === currentSortField) {
            dt.sortSingle()
          }
        }, 0)
      }
      return
    }

    if (matchingStatusCodes && matchingStatusCodes.length > 0) {
      // Status-based filter: handle entirely in table, don't emit to parent
      // (parent's application.getResources doesn't understand status keywords)
      dt.filter(matchingStatusCodes, 'status', 'in')
    } else {
      // Text-based filter: use global filter and emit to parent
      dt.filterGlobal(filterValue, 'contains')
      // Only emit to parent for text-based filters (not status keywords)
      if (filterValue !== this.lastEmittedFilterText) {
        this.lastEmittedFilterText = filterValue
        this.filterTextChange.emit(filterValue)
      }
    }

    // Restore sort state after applying filters
    if (currentSortField) {
      dt.sortField = currentSortField
      dt.sortOrder = currentSortOrder
      // Force PrimeNG to re-evaluate sort state
      setTimeout(() => {
        if (dt.sortField === currentSortField) {
          dt.sortSingle()
        }
      }, 0)
    }

    this.filterText = filterValue
    this.isFilterApplied = true
  }

  clear(table: Table) {
    this.isFilterApplied = false
    this.selectedCategories = []
    this.selectedStatuses = []
    // Only clear filters, preserve sort state
    table.clearFilterValues()
    // Clear the input field
    if (this.filterInput && this.filterInput.nativeElement) {
      this.filterInput.nativeElement.value = ''
    }
  }

  clearInput(table: Table) {
    this.filterText = ''
    this.filterInput.nativeElement.value = ''
    this.clear(table)
    // Emit change to parent for persistence
    if ('' !== this.lastEmittedFilterText) {
      this.lastEmittedFilterText = ''
      this.filterTextChange.emit('')
    }
  }

  clearFilterValues(dt1: Table, categoryToClear: string) {
    if (!categoryToClear) return

    dt1.clearFilterValues()
    categoryToClear === 'selectedStatuses' ? (this.selectedStatuses = []) : (this.selectedCategories = [])
  }

  resourceTrackBy(index: number, resource: ViewableApplicationResource) {
    return resource.resourceArn
  }

  toggleRow(resourceArn: string) {
    if (this.expandedRows.has(resourceArn)) {
      this.expandedRows.delete(resourceArn)
    } else {
      this.expandedRows.add(resourceArn)
      this.fetchResourceConfig(resourceArn)
    }
  }

  isS3Bucket(resource: ViewableApplicationResource) {
    return resource.resourceType == ResourceType.s3Bucket
  }

  fetchResourceConfig(resourceArn: string) {
    // Kick off background fetch of resource config
    this.application
      .getSyncPairResourceConfig$(resourceArn)
      .pipe()
      .subscribe(
        (resourceConfig) => {
          if (!!resourceConfig) {
            this.resourceConfigs.set(resourceArn, resourceConfig.resourceConfig)
          } else {
            this.resourceConfigs.set(resourceArn, null)
          }
        },
        handleArpioError(window.location.href, null, this.uiService)
      )
  }

  isExpandedRow(resourceArn: string) {
    return this.expandedRows.has(resourceArn)
  }

  s3ReplicationMode(resource: ViewableApplicationResource) {
    // Returns string to display for the S3 bucket's replication mode

    if (this.resourceConfigs.has(resource.resourceArn)) {
      let s3Config = this.resourceConfigs.get(resource.resourceArn) as S3BucketConfig
      return S3_OBJECT_REPLICATION_MODES.get(s3Config?.objectReplicationMode)
    } else {
      // No entry in map, we are still waiting for it to be fetched from the server.
      return 'Loading...'
    }
  }

  get emptyText(): string {
    if (this.tabType === ResourcesTab.FAILBACK) {
      if (!!this.appStatus && this.appStatus.inProgress) {
        return 'Failback started, updating resource status...'
      } else {
        return 'Once you start a failback, resource status will appear here.'
      }
    } else {
      return 'No resources found.'
    }
  }

  get shouldShowResourceGroupColumn() {
    return this._shouldShowResourceGroupColumn
  }

  get globalFilterFields() {
    return this._globalFilterFields
  }

  get notEmptyList() {
    return this._notEmptyList
  }

  get isFailbackTab() {
    return this.tabType === ResourcesTab.FAILBACK
  }

  private updateGlobalFilterFields() {
    const baseFields = ['status', 'displayResourceType']
    const resourceGroupFields = []

    if (this._shouldShowResourceGroupColumn && !this.appIsDeleted) {
      resourceGroupFields.push('sourceResourceGroup')
    }

    const resourceNameFields = this.appIsDeleted ? ['targetResourceName'] : ['sourceResourceName', 'targetResourceName']

    this._globalFilterFields = [...baseFields, ...resourceGroupFields, ...resourceNameFields]
  }

  private updateNotEmptyList() {
    if (!this.resources) {
      this._notEmptyList = false
      return
    }
    this._notEmptyList =
      (this.tabType !== ResourcesTab.FAILBACK && this.resources.length > 0) ||
      (this.tabType === ResourcesTab.FAILBACK && this.resources.some((val) => val.status !== ResourceStatus.NO_STATUS))
  }
}
